import { ChangeDetectionStrategy, Component, input, output, signal } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { TimeAgoModule } from '@examdojo/core/date-time';
import { MarkschemeInfo } from '@examdojo/question';
import { QuestionType } from '../review-item';
import { ConsensusFeedbackFormComponent, QuestionReviewFeedbackFormState } from './consensus-feedback-form.component';
import { Evaluation } from './evaluation.model';
import { QuestionReviewTypeBadgeComponent } from './question-review-type-badge.component';
import { ReferenceQuestionReviewTypeBadgeComponent } from './reference-question-review-type-badge.component';

@Component({
  selector: 'dojo-previous-evaluations',
  standalone: true,
  imports: [
    MatExpansionModule,
    TimeAgoModule,
    ConsensusFeedbackFormComponent,
    QuestionReviewTypeBadgeComponent,
    ReferenceQuestionReviewTypeBadgeComponent,
  ],
  template: `
    <div class="flex h-full flex-col">
      <div class="scrollbar-gutter-stable flex-1 overflow-auto p-4 pr-3">
        @if (evaluations() && evaluations().length > 0) {
          <mat-accordion multi>
            @for (evaluation of evaluations(); track evaluation.id) {
              <mat-expansion-panel
                [expanded]="expandedEvaluationId() === evaluation.id"
                (opened)="expandedEvaluationId.set(evaluation.id)"
                (closed)="expandedEvaluationId.set(null)"
                class="custom-expansion-panel"
              >
                <mat-expansion-panel-header [collapsedHeight]="'90px'" [expandedHeight]="'90px'">
                  <mat-panel-title>
                    <div class="flex w-full items-center justify-between pr-6">
                      <div class="min-w-0 flex-1">
                        <div class="flex items-center gap-2">
                          Mode:
                          @if (evaluation.questionType === QuestionType.Derivative) {
                            <dojo-question-review-type-badge [reviewType]="evaluation.reviewType" />
                          } @else {
                            <dojo-reference-question-review-type-badge [reviewType]="evaluation.reviewType" />
                          }
                        </div>
                        <div class="mt-0.5 text-xs text-neutral-400 dark:text-neutral-300">
                          <span class="font-medium">Evaluator:</span>
                          @if (evaluation.user_first_name || evaluation.user_last_name) {
                            {{ evaluation.user_first_name }} {{ evaluation.user_last_name }}
                            <span class="text-neutral-300 dark:text-neutral-400">({{ evaluation.user_email }})</span>
                          } @else {
                            {{ evaluation.user_email }}
                          }
                        </div>
                      </div>
                      <div class="max-w-[80px] flex-shrink-0 text-right">
                        <div class="text-xs text-neutral-400 dark:text-neutral-300">
                          <dojo-time-ago [date]="evaluation.created_at" />
                        </div>
                      </div>
                    </div>
                  </mat-panel-title>
                </mat-expansion-panel-header>

                <dojo-consensus-feedback-form
                  class="border-t border-neutral-200 dark:border-neutral-700"
                  [activeReviewMode]="evaluation.reviewType"
                  [calculatorAllowed]="calculatorAllowed()"
                  [checklistConfig]="evaluation.config"
                  [feedback]="evaluation.feedback"
                  [isReadonly]="true"
                  [markschemeInfo]="markschemeInfo()"
                  (formStateChange)="formStateChange.emit($event)"
                />
              </mat-expansion-panel>
            }
          </mat-accordion>
        } @else {
          <div class="py-8 text-center text-neutral-400 dark:text-neutral-300">
            {{ emptyMessage() }}
          </div>
        }
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      ::ng-deep .mat-accordion {
        // remove default material spacing
        .mat-expansion-panel:not(:first-child) {
          margin-top: 0 !important;
        }

        // remove space-y conflicts
        & > * + * {
          margin-top: 0 !important;
        }
      }

      ::ng-deep .custom-expansion-panel {
        border: 1px solid theme('colors.neutral.200');
        border-radius: 12px; // rounder corners
        box-shadow: none !important; // no shadows
        margin-y: 8px;

        &:not(.mat-expanded) {
          border-bottom: 1px solid theme('colors.neutral.200');
          margin: 4px;
        }

        .mat-expansion-panel-header {
          padding: 12px 16px;

          &:hover {
            background-color: theme('colors.neutral.50');
          }
        }

        .mat-expansion-panel-body {
          padding: 0 16px 8px 8px;
        }

        .mat-panel-title {
          margin: 0;
          width: 100%;
        }

        // when expanded, header bottom corners become square
        &.mat-expanded .mat-expansion-panel-header {
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        }
      }
    `,
  ],
})
export class PreviousEvaluationsComponent {
  readonly evaluations = input.required<Evaluation[]>();
  readonly calculatorAllowed = input.required<boolean>();
  readonly markschemeInfo = input.required<MarkschemeInfo[]>();
  readonly emptyMessage = input('No previous evaluations found.');

  readonly formStateChange = output<QuestionReviewFeedbackFormState>();

  readonly expandedEvaluationId = signal<string | null>(null);
  readonly QuestionType = QuestionType;
}
